import { runInAction } from 'mobx';
import { AxiosResponse } from 'axios';

import { notUndefined } from 'ui/base/types';

import {
  createRequestString,
  getResponseBytes,
  http,
} from 'services/base/http';

import {
  DeletePaymentRequest,
  DeletePaymentResponse,
  PaymentListResponse,
} from './protos/payment_pb';

import {
  createPaymentObject,
  createPaymentObjects,
  IAccountsDomain,
  Payment,
  PaymentMap,
  paymentSorter,
} from './types';

export class PaymentsService {
  static async fetchAll(domain: IAccountsDomain, save = true): Promise<PaymentMap> {
    const httpResp = await http.get('/billing/payments/');
    const remotePayments = parsePaymentsResponse(httpResp);
    remotePayments.sort(paymentSorter);
    return runInAction(() => {
      if (save) domain.payments = remotePayments;
      return remotePayments;
    });
  }

  static async delete(domain: IAccountsDomain,
                      payment: Payment,
                      password: string): Promise<{ success: true } |
                                                 { success: false,
                                                   status?: number }> {
    // Determine location of appropriate endpoint
    const endpoint = `/billing/payments/${payment.id}`;

    // Construct the request payload
    const requestProto = new DeletePaymentRequest();
    requestProto.setPassword(password);

    // Issue the request to the server
    const rawPayload = createRequestString(requestProto);
    return await http.delete(endpoint, { data: rawPayload }).then((httpResponse) => {
      // Parse the response from the server
      const responseProto = DeletePaymentResponse.deserializeBinary(getResponseBytes(httpResponse));

      // Retrieve the new Payment object from the response
      const deletedPaymentDTO = responseProto.getPayment()!;
      const deletedPayment = createPaymentObject(deletedPaymentDTO);

      // Update the PaymentMap with the new Payment object
      runInAction(() => { domain.payments.delete(deletedPayment.id); });

      // Return true on success
      return { success: true };
    }).catch((httpError) => {
      // Return false on failure
      return { success: false, status: httpError.response?.status };
    });
  }
}

function parsePaymentsResponse(httpResp: AxiosResponse): PaymentMap {
  const rawBytes = getResponseBytes(httpResp);
  const rootProto = PaymentListResponse.deserializeBinary(rawBytes);
  const paymentsObjects = createPaymentObjects(rootProto.getPaymentsList());
  return new PaymentMap(paymentsObjects.filter(notUndefined).map((p) => [p.id, p]));
}
